import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  getAdminAccountDetail,
  updateAdminAccount,
  accountQuickAction,
  removeAccountMember,
  deleteAdminAccount,
} from "@/lib/admin-account-detail.functions";
import { startImpersonation } from "@/lib/impersonation.functions";
import { Trash2 } from "lucide-react";

export const Route = createFileRoute(
  "/_authenticated/admin/accounts/$accountId",
)({
  component: AccountDetailPage,
});

const PLANS = [
  "trial",
  "starter_affiliate",
  "pro_affiliate",
  "super_affiliate",
  "comped",
  "paused",
];
const STATUSES = ["active", "past_due", "canceled", "paused"];
const BILLING = ["monthly", "annual"];

function AccountDetailPage() {
  const { accountId } = Route.useParams();
  const qc = useQueryClient();
  const fetchDetail = useServerFn(getAdminAccountDetail);
  const update = useServerFn(updateAdminAccount);
  const quick = useServerFn(accountQuickAction);
  const removeMember = useServerFn(removeAccountMember);
  const deleteAccount = useServerFn(deleteAdminAccount);
  const navigate = useNavigate();
  const startImp = useServerFn(startImpersonation);
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-account-detail", accountId],
    queryFn: () => fetchDetail({ data: { accountId } }),
  });

  if (isLoading || !data) {
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  }
  const acct = data.account;

  function refresh() {
    qc.invalidateQueries({ queryKey: ["admin-account-detail", accountId] });
    qc.invalidateQueries({ queryKey: ["admin-accounts"] });
  }

  async function doQuick(action: any, extra?: { days?: number; reason?: string }) {
    try {
      await quick({
        data: { accountId, action, ...(extra ?? {}) },
      });
      toast.success("Done");
      refresh();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed");
    }
  }

  return (
    <div className="space-y-6">
      <Link
        to="/admin/accounts"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back to accounts
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">{acct.name}</h2>
          <p className="mt-1 font-mono text-xs text-muted-foreground">{acct.id}</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            <Badge variant="secondary">{acct.plan}</Badge>
            <Badge variant={acct.plan_status === "active" ? "default" : "outline"}>
              {acct.plan_status}
            </Badge>
            {acct.is_admin_comped && <Badge variant="outline">comped</Badge>}
            {acct.billing_interval && (
              <Badge variant="outline">{acct.billing_interval}</Badge>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {acct.plan_status !== "paused" ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                doQuick("pause", { reason: prompt("Reason?") ?? undefined })
              }
            >
              Pause
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={() => doQuick("unpause")}>
              Unpause
            </Button>
          )}
          {acct.is_admin_comped ? (
            <Button variant="outline" size="sm" onClick={() => doQuick("uncomp")}>
              Remove comp
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={() => doQuick("comp")}>
              Mark complimentary
            </Button>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const raw = prompt("Extend trial by how many days?", "14");
              const days = raw ? parseInt(raw, 10) : NaN;
              if (Number.isFinite(days) && days > 0)
                doQuick("extend_trial", { days });
            }}
          >
            Extend trial
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={async () => {
              const reason = prompt(
                "Reason for viewing as this account (audit log):",
              );
              if (!reason || reason.trim().length < 3) return;
              try {
                await startImp({
                  data: { targetAccountId: accountId, reason: reason.trim() },
                });
                toast.success("View-as mode active");
                qc.invalidateQueries({ queryKey: ["impersonation-active"] });
              } catch (e) {
                toast.error(e instanceof Error ? e.message : "Failed");
              }
            }}
          >
            View as account
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={async () => {
              const confirmName = prompt(
                `This will permanently delete the account "${acct.name}" and all its workspaces, contacts, and data. This cannot be undone.\n\nType the account name exactly to confirm:`,
              );
              if (!confirmName || confirmName.trim() !== acct.name) {
                if (confirmName !== null) toast.error("Name did not match. Cancelled.");
                return;
              }
              const reason = prompt("Reason (audit log):", "Admin deletion") ?? undefined;
              try {
                const res = await deleteAccount({
                  data: {
                    accountId,
                    confirmName: confirmName.trim(),
                    reason: reason?.trim() || undefined,
                  },
                });
                toast.success(
                  `Account deleted${res.auth_users_deleted ? ` · ${res.auth_users_deleted} login(s) removed` : ""}`,
                );
                qc.invalidateQueries({ queryKey: ["admin-accounts"] });
                navigate({ to: "/admin/accounts" });
              } catch (e) {
                toast.error(e instanceof Error ? e.message : "Failed");
              }
            }}
          >
            Delete account
          </Button>
        </div>
      </div>

      <EditPanel
        account={acct}
        onSubmit={async (patch, reason, notify) => {
          try {
            await update({
              data: { accountId, patch, reason, notifyAccount: notify },
            });
            toast.success("Account updated");
            refresh();
          } catch (e) {
            toast.error(e instanceof Error ? e.message : "Update failed");
          }
        }}
      />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Card className="p-4">
          <div className="text-xs uppercase text-muted-foreground">
            Workspaces
          </div>
          <div className="mt-1 text-2xl font-semibold">
            {data.workspaces.length}{" "}
            <span className="text-sm text-muted-foreground">
              / {acct.workspace_limit}
            </span>
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-xs uppercase text-muted-foreground">Members</div>
          <div className="mt-1 text-2xl font-semibold">
            {data.members.length}{" "}
            <span className="text-sm text-muted-foreground">
              / {acct.team_member_limit}
            </span>
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-xs uppercase text-muted-foreground">
            AI spend (30d)
          </div>
          <div className="mt-1 text-2xl font-semibold tabular-nums">
            ${data.ai_30d.cost_usd.toFixed(2)}
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            {data.ai_30d.calls.toLocaleString()} calls ·{" "}
            {data.ai_30d.cache_hits.toLocaleString()} cache hits
          </div>
        </Card>
      </div>

      <Card className="p-4">
        <h3 className="text-sm font-semibold">AI usage by capability (30d)</h3>
        {data.ai_by_capability.length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">No usage.</p>
        ) : (
          <table className="mt-3 w-full text-sm">
            <thead className="text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="py-1 pr-3">Capability</th>
                <th className="py-1 pr-3 text-right">Calls</th>
                <th className="py-1 pr-3 text-right">Cache hits</th>
                <th className="py-1 pr-3 text-right">Cost</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.ai_by_capability.map((c) => (
                <tr key={c.capability}>
                  <td className="py-1.5 pr-3">{c.capability}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">
                    {c.calls}
                  </td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">
                    {c.cache_hits}
                  </td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">
                    ${c.cost_usd.toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card className="p-4">
        <h3 className="text-sm font-semibold">Members ({data.members.length})</h3>
        <table className="mt-3 w-full text-sm">
          <thead className="text-left text-xs uppercase text-muted-foreground">
            <tr>
              <th className="py-1 pr-3">Name</th>
              <th className="py-1 pr-3">Email</th>
              <th className="py-1 pr-3">Role</th>
              <th className="py-1 pr-3">Joined</th>
              <th className="py-1 pr-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {data.members.map((m) => (
              <tr key={m.user_id}>
                <td className="py-1.5 pr-3">{m.full_name ?? ","}</td>
                <td className="py-1.5 pr-3 text-muted-foreground">{m.email}</td>
                <td className="py-1.5 pr-3">
                  <Badge variant="outline" className="text-xs">
                    {m.role}
                  </Badge>
                </td>
                <td className="py-1.5 pr-3 text-xs text-muted-foreground">
                  {new Date(m.joined_at).toLocaleDateString()}
                </td>
                <td className="py-1.5 pr-3 text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                    disabled={deletingUserId === m.user_id}
                    onClick={async () => {
                      const label = m.email ?? m.full_name ?? "this user";
                      if (
                        !confirm(
                          `Delete ${label}? This removes them from this account. If they belong to no other account, their login will also be deleted permanently. This cannot be undone.`,
                        )
                      )
                        return;
                      const reason =
                        prompt("Reason (audit log):", "Admin removal") ?? undefined;
                      setDeletingUserId(m.user_id);
                      try {
                        const res = await removeMember({
                          data: {
                            accountId,
                            userId: m.user_id,
                            reason: reason?.trim() || undefined,
                          },
                        });
                        toast.success(
                          res.auth_user_deleted
                            ? "User removed and login deleted"
                            : "User removed from account",
                        );
                        refresh();
                      } catch (e) {
                        toast.error(e instanceof Error ? e.message : "Failed");
                      } finally {
                        setDeletingUserId(null);
                      }
                    }}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Card className="p-4">
        <h3 className="text-sm font-semibold">
          Workspaces ({data.workspaces.length})
        </h3>
        <ul className="mt-3 divide-y divide-border text-sm">
          {data.workspaces.map((w) => (
            <li key={w.id} className="flex items-center justify-between py-1.5">
              <span>{w.program_name}</span>
              <span className="font-mono text-xs text-muted-foreground">
                {w.id}
              </span>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}

function EditPanel({
  account,
  onSubmit,
}: {
  account: any;
  onSubmit: (
    patch: Record<string, any>,
    reason: string | undefined,
    notify: boolean,
  ) => Promise<void>;
}) {
  const [plan, setPlan] = useState<string>(account.plan);
  const [status, setStatus] = useState<string>(account.plan_status);
  const [billing, setBilling] = useState<string>(account.billing_interval ?? "none");
  const [wsLimit, setWsLimit] = useState<number>(account.workspace_limit);
  const [memLimit, setMemLimit] = useState<number>(account.team_member_limit);
  const [trialEnds, setTrialEnds] = useState<string>(
    account.trial_ends_at
      ? new Date(account.trial_ends_at).toISOString().slice(0, 16)
      : "",
  );
  const [reason, setReason] = useState("");
  const [notify, setNotify] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit() {
    const patch: Record<string, any> = {};
    if (plan !== account.plan) patch.plan = plan;
    if (status !== account.plan_status) patch.plan_status = status;
    const billingValue = billing === "none" ? null : billing;
    if (billingValue !== (account.billing_interval ?? null))
      patch.billing_interval = billingValue;
    if (wsLimit !== account.workspace_limit) patch.workspace_limit = wsLimit;
    if (memLimit !== account.team_member_limit)
      patch.team_member_limit = memLimit;
    const newTrial = trialEnds ? new Date(trialEnds).toISOString() : null;
    const curTrial = account.trial_ends_at ?? null;
    if (newTrial !== curTrial) patch.trial_ends_at = newTrial;

    if (Object.keys(patch).length === 0) {
      toast.info("No changes");
      return;
    }
    setBusy(true);
    try {
      await onSubmit(patch, reason.trim() || undefined, notify);
      setReason("");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="p-5">
      <h3 className="text-sm font-semibold">Plan & limits</h3>
      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
        <div>
          <Label>Plan</Label>
          <Select value={plan} onValueChange={setPlan}>
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PLANS.map((p) => (
                <SelectItem key={p} value={p}>
                  {p}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Status</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Billing interval</Label>
          <Select value={billing} onValueChange={setBilling}>
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">,</SelectItem>
              {BILLING.map((b) => (
                <SelectItem key={b} value={b}>
                  {b}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Workspace limit</Label>
          <Input
            type="number"
            min={0}
            value={wsLimit}
            onChange={(e) => setWsLimit(parseInt(e.target.value || "0", 10))}
            className="mt-1"
          />
        </div>
        <div>
          <Label>Team member limit</Label>
          <Input
            type="number"
            min={0}
            value={memLimit}
            onChange={(e) => setMemLimit(parseInt(e.target.value || "0", 10))}
            className="mt-1"
          />
        </div>
        <div>
          <Label>Trial ends at</Label>
          <Input
            type="datetime-local"
            value={trialEnds}
            onChange={(e) => setTrialEnds(e.target.value)}
            className="mt-1"
          />
        </div>
        <div className="md:col-span-3">
          <Label>Reason (audit log)</Label>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why this change?"
            className="mt-1 min-h-16"
            maxLength={500}
          />
        </div>
        <div className="flex items-center gap-2 md:col-span-3">
          <Switch checked={notify} onCheckedChange={setNotify} id="notify-acct" />
          <Label htmlFor="notify-acct" className="cursor-pointer text-sm">
            Notify account members in-app
          </Label>
        </div>
      </div>
      <div className="mt-4 flex justify-end">
        <Button onClick={submit} disabled={busy}>
          {busy ? "Saving…" : "Save changes"}
        </Button>
      </div>
    </Card>
  );
}
